<template>
  <DefaultPageContainer
    :cnc-ads-page="cncAdsPage"
    :breadcrumb-text="breadcrumbsItem"
  >

    <section class="section">
      <DefaultTwoColumnsContent
        right-container-class="section__right-col--mobile-hide"
      >
        <template #left>
          <div class="flex flex-col gap-6">
            <div v-if="articleStore.article" class="articleid">

              <DefaultArticlesArticleDetail
                :article="articleStore.article"
                :cnc-ads-page="cncAdsPage"
              />

              <!-- Related articles container -->
              <DefaultRecommended
                :title="$t('articles.related_articles_title')"
                :placement-id="RecommendPlacementId.ARTICLE_DETAIL_RELATED"
                :articles-count="pageRelatedArticlesCount"
                :item-id="articleStore.article?.id"
              />

              <!-- Recommended articles container -->
              <DefaultRecommended
                :title="$t('articles.external_articles_title')"
                :placement-id="RecommendPlacementId.ARTICLE_DETAIL_EXTERNAL"
                :articles-count="pageRecommendArticlesCount"
              />
            </div>
            <div v-else-if="articleStore.isLoading">
              <DefaultSkeletonsArticleDetail :type="ArticleComponentType.BIG"/>
            </div>
          </div>
        </template>
        <template #right>
          <!-- CNC Ads -->
          <div class="advertisement-right-col h-full">
            <div class="sticky-sidebar-ad-wrapper">
              <DefaultAdsCncWrapper
                :position="UniFeAdsPosition.COLUMN_STICKY"
                :page="cncAdsPage"
              />
            </div>
          </div>
        </template>
      </DefaultTwoColumnsContent>
    </section>
  </DefaultPageContainer>
</template>

<script setup lang="ts">
import {ARTICLE_QUERY_TYPE, useArticlesStore} from "~/stores/useArticlesStore"
import {ArticleStoreType} from "~/libs/constants/ArticleStoreType"
import ArticleComponentType from "~/libs/constants/ArticleComponentType"
import {RecommendPlacementId} from "~/libs/constants/RecommendPlacementId"
import {useRecommendArticlesStore} from "~/stores/useRecommendArticlesStore"
import {useHtmlToPlainText} from "~/composables/useHtmlToPlainText"
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"
import {DEBUG_COOKIE} from "~/libs/constants/constants"
import {decode} from 'html-entities'
import {useSeoWrapper} from "~/composables/useSeoWrapper"

// i18n routing
definePageMeta({
  name: 'articles-articleUrl',
  middleware: [
    function (to, from) {
      const localePath = useLocalePath()
      let newPath = decodeURIComponent(to.params.articleUrl as string)

      if (newPath?.at(0) === '/') {
        newPath = newPath.slice(1, newPath.length)
      }
      if (newPath?.at(newPath?.length-1) === '/') {
        newPath = newPath.slice(0, newPath.length-1)
      }

      const currentPath = to.fullPath.split('?')[0]

      const localePathUrl = decodeURIComponent(localePath({name: 'articles-articleUrl', params: {articleUrl: encodeURIComponent(newPath)}}))
      if (currentPath !== localePathUrl) {
        const debugCookie = useCookie(DEBUG_COOKIE)
        if (debugCookie) {
          const logger = useFrontendLogger()
          logger?.info('Page moved - 301', {
            originalPath: to.fullPath,
            newPath: localePathUrl
          })
        }
        return navigateTo(localePathUrl, { redirectCode: 301 })
      }
    },
  ],
})
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    cs: '/clanek/[articleUrl]',
    en: '/article/[articleUrl]'
  }
})

const config = useRuntimeConfig()
const route = useRoute()
const logger = useFrontendLogger()
const cncAdsPage = CncAdsPage.ARTICLE_DETAIL

const debugCookie = useCookie(DEBUG_COOKIE)

const recommendedEnabled = config.public.recombee?.enabled
const articleStore = useArticlesStore(ArticleStoreType.ARTICLE_DETAIL)

// const recommTypeExternal = RecommendPlacementId.ARTICLE_DETAIL_EXTERNAL
// const recommendArticlesStoreExternal = useRecommendArticlesStore(recommTypeExternal)

const recommTypeRelated = RecommendPlacementId.ARTICLE_DETAIL_RELATED
const recommendArticlesStoreRelated = useRecommendArticlesStore(recommTypeRelated)

const mainImage = computed(() => {
  return articleStore.article?.previewAsset?.urlWithProtocol || articleStore.article?.articleLinkAssetList?.edges?.at(0)?.node?.asset?.urlWithProtocol || null
})

const breadcrumbsItem = computed(() => {
  return articleStore.article?.categoryList?.edges?.at(0)?.node?.name || articleStore.article?.title || undefined
})

watch(
  () => {
    return articleStore.article?.id
  }, async (newValue) => {
    if (newValue) {
      setSeoMeta()
    }
  }
)

const seoWrapper = useSeoWrapper()

// Set Seo meta data
const setSeoMeta = () => {
  // Set SEO meta
  seoWrapper.setSeoMeta({
    title: decode(articleStore.article?.title),
    ...(articleStore.article?.perex && {
      description: useHtmlToPlainText(articleStore.article?.perex),
    }),
    ogImage: () => mainImage.value,
  })
}

const getAllArticleUrls = () => {
  const path = decodeURIComponent(route.params.articleUrl as string)
  return [
    `${path}`,
    `/${path}`,
    `/${path}/`,
    `${path}/`,
  ]
}

articleStore.setLimit(1)
const filters = {
  paths: getAllArticleUrls()
}

try {
  if (import.meta.server) {
    await articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.DETAIL, {
      forceRefetch: true,
      useDefaultFilters: false,
      usePublishedStateFilters: false,
    })
    setSeoMeta()
  }
} catch (e) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

/**
 * Related local articles
 */
const pageRelatedArticlesCount = Number(config.public.articleRelatedArticlesCount || 5)
const pageRecommendArticlesCount = Number(config.public.articleRecommendArticlesCount || 5)

onMounted(async () => {
  // load only when path changed
  if (!articleStore.article?.path || !filters.paths.includes(articleStore.article?.path)) {
    try {
      await articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.DETAIL, {
        forceRefetch: true,
        useDefaultFilters: false,
        usePublishedStateFilters: false,
      })
      setSeoMeta()
    } catch (e) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found'
      })
    }
  }

  if (debugCookie) {
    logger?.info('Loaded Article', articleStore.article)
  }

  if (recommendedEnabled && articleStore.article?.id) {
    recommendArticlesStoreRelated.addDetailView(articleStore.article?.id)
  }

  /**
   * CncAds Page change
   */
  useCncAdsWrapper().changePageAds(cncAdsPage, {
    route: route
  })
})

</script>
